import { CheckCheck, ListChecks, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import EditableBrandColorSwatch from "~/components/EditableBrandColorSwatch";

interface BrandSelectorProps {
  selectedBrands: string[];
  brandOptions: string[];
  brandColorMap: Record<string, string>;
  teamDomain: string;
  onBrandColorSaved: (domain: string, color: string) => void;
  onBrandsChange: (brands: string[]) => void;
  className?: string;
}

export default function BrandSelector({
  selectedBrands,
  brandOptions,
  brandColorMap,
  teamDomain,
  onBrandColorSaved,
  onBrandsChange,
  className = "",
}: BrandSelectorProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={className}>
          {selectedBrands.length > 0 ? (
            <>
              <div className="flex items-center -space-x-2 mr-2">
                {selectedBrands.slice(0, 3).map((brand, idx) => (
                  <div
                    key={brand}
                    className="relative"
                    style={{ zIndex: 3 - idx }}
                  >
                    <img
                      src={`https://img.logo.dev/${brand}?token=${
                        import.meta.env.VITE_LOGO_DOT_DEV_PUBLISHABLE_API_KEY
                      }`}
                      alt={brand}
                      className="w-5 h-5 rounded-full object-cover ring-2 ring-white"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "https://www.gravatar.com/avatar/?d=mp&s=32";
                      }}
                    />
                  </div>
                ))}
                {selectedBrands.length > 3 && (
                  <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                    +{selectedBrands.length - 3}
                  </div>
                )}
              </div>
              <span className="text-sm">
                {selectedBrands.length === 1
                  ? selectedBrands[0].split(".")[0]
                  : `${selectedBrands.length} brand${
                      selectedBrands.length > 1 ? "s" : ""
                    }`}
              </span>
            </>
          ) : (
            <>
              <ListChecks className="w-4 h-4 text-muted-foreground mr-2" />
              <span className="text-sm">Select brands</span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[280px] max-w-[420px] overflow-hidden p-0">
        <div className="flex items-center justify-between space-x-2 px-3 py-2 border-b">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Select Brands</span>
            {selectedBrands.length > 0 && (
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium bg-muted rounded-md">
                {selectedBrands.length}/{brandOptions.length}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onBrandsChange(brandOptions)}
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onBrandsChange([])}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear
            </Button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          <div className="flex flex-col gap-1">
            {brandOptions.map((brand) => (
              <label
                key={brand}
                className="flex items-center space-x-3 py-2 px-3 cursor-pointer rounded-md hover:bg-accent transition-colors"
              >
                <Checkbox
                  id={brand}
                  checked={selectedBrands.includes(brand)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const newBrands = Array.from(
                        new Set([...(selectedBrands || []), brand])
                      );
                      onBrandsChange(newBrands);
                    } else {
                      const newBrands = selectedBrands.filter(
                        (b) => b !== brand
                      );
                      onBrandsChange(newBrands);
                    }
                  }}
                />

                <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                  <EditableBrandColorSwatch
                    domain={brand}
                    color={brandColorMap[brand] || "#9ca3af"}
                    teamDomain={teamDomain}
                    size="sm"
                    onSaved={onBrandColorSaved}
                  />
                  <img
                    src={`https://img.logo.dev/${brand}?token=${
                      import.meta.env.VITE_LOGO_DOT_DEV_PUBLISHABLE_API_KEY
                    }`}
                    alt={brand}
                    className="h-5 w-5 rounded-md object-cover flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "https://www.gravatar.com/avatar/b257f63497f9ca9d197dcbad473dc3f8?s=64&d=mm&r=g";
                    }}
                  />
                  <span className="text-sm whitespace-nowrap truncate">
                    {brand}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>
        {brandOptions.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No brands available
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
